import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Switch, useHistory } from 'react-router-dom';
import { useUpdate } from 'react-use';

import { Spinner } from './components/ui/spinner';
import { ADZ_REDIRECT_URI } from './config';
import { AutoLoginProvider } from './context/AutoLoginContext';
import { DashboardLayout } from './layout';
import { Login } from './pages/login';
import { PageNotFound } from './pages/page-not-found';
import { Register } from './pages/register';
import { ResetPassword } from './pages/reset-password';
import { UserSetup } from './pages/setup';
import { AuthenticatedRoute, UnauthenticatedRoute } from './routes/guards';
import { authService } from './services/auth';
import { authState } from './services/auth-state';
import { clearCSRFCookies } from './utils/cookie';

const RootRedirect = (): JSX.Element => {
  const history = useHistory();

  useEffect(() => {
    let cancelled = false;

    const params = new URLSearchParams(window.location.search);
    const serviceRedirectUrl = params.get('service_redirect_url') ?? undefined;
    const clientId = params.get('client_id') ?? undefined;

    const runRedirect = async () => {
      try {
        const { redirectUrl } = await authService.getPostLoginRedirect(serviceRedirectUrl, clientId);
        if (cancelled) {
          return;
        }

        if (redirectUrl.startsWith('/')) {
          history.replace(redirectUrl);
        } else {
          window.location.replace(redirectUrl);
        }
      } catch {
        // The validated post-login redirect was rejected: the service_redirect_url
        // is not whitelisted for this client_id, the user is an API-token user, or a
        // transient error occurred. Never fall back to an unvalidated external
        // redirect (that would be an open redirect) — only send the user to a safe
        // first-party destination.
        if (serviceRedirectUrl && serviceRedirectUrl !== '/') {
          history.replace('/admin');
        } else {
          window.location.replace(ADZ_REDIRECT_URI);
        }
      }
    };

    runRedirect();

    return () => {
      cancelled = true;
    };
  }, [history]);

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
      <Spinner size="lg" centered />
    </div>
  );
};

function Routes() {
  const forceUpdate = useUpdate();

  useEffect(() => {
    authState.registerSubscriber(forceUpdate); // by forcing rerender when loggedin state changes UnauthenticatedRoute and AuthenticatedRoute can redirect properly
    return () => authState.unregisterSubscriber(forceUpdate);
  }, [forceUpdate]);

  return (
    <Switch>
      <Route
        exact
        path="/page-not-found"
        render={({ location }) => {
          const from: string = (location.state as any)?.from ?? '';
          return <PageNotFound from={from} />;
        }}
      />
      <UnauthenticatedRoute exact path="/login" component={Login} />
      <UnauthenticatedRoute exact path="/register" component={Register} />
      {/* /reset-password and /setup are reached from emailed links and carry a
          one-time ?token=. They are deliberately NOT UnauthenticatedRoute: that
          guard redirects a signed-in visitor to '/' and drops the token, so the
          link appears to do nothing. Each page renders <AlreadyLoggedIn /> for a
          signed-in visitor instead, and continues once they log out. */}
      <Route exact path="/reset-password" component={ResetPassword} />
      <Route exact path="/setup" component={UserSetup} />
      <AuthenticatedRoute path="/admin" component={DashboardLayout} />
      <AuthenticatedRoute exact path="/" component={RootRedirect} />
    </Switch>
  );
}

export function App(): JSX.Element {
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      clearCSRFCookies(); // Clear CSRF cookies on app load to prevent issues with stale tokens
      await authService.loadLoggedInState(); // this gets new csrf token as well
      setLoading(false);
    }

    load();
  }, []);

  return isLoading ? (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
      <div className="flex items-center justify-center">
        <Spinner size="lg" centered />
      </div>
    </div>
  ) : (
    <BrowserRouter>
      <AutoLoginProvider>
        <Routes />
      </AutoLoginProvider>
    </BrowserRouter>
  );
}
